import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalHeader,
  Tile,
} from "@carbon/react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Award,
  GoBackArrow,
  Loader,
  Offer,
  Chat,
  AlertBanner,
  NetworkError,
  Pill,
  SecondRoundModal,
} from "../../components";
import SecondRoundTag from "../../components/UI/SecondRoundTag/SecondRoundTag";
import InviteSupplier from "../../components/Material/InviteSupplier/InviteSupplier";
import { useID } from "../../hooks";
import OffersOnOrders from "./OffersOnOrders";
import SuppliersSelected from "./SuppliersSelected";
import BidHeader from "./BidHeader";
import BidResultTable from "./BidResultTable";
import BidToolbar from "./BidToolbar";
import BidAwardedTabs from "./BidAwardedTabs";
import CancelOrderDialog from "./CancelOrderDialog";
import FinalizeRecapModal from "./FinalizeRecapModal";
import { OrderConditionsTile, OfferSummaryTile } from "./BidSummaryTiles";
import { MaterialsOverviewTile } from "./MaterialsOverview";
import ActivityTimeline from "../../components/ActivityTimeline/ActivityTimeline";
import { OfferResponse, OrderState } from "../../types";
import {
  useDownloadBidReport,
  useDownloadOrderComparison,
  useDeleteOrder,
  useOrder,
  useOrderActivityLog,
  useOrderSupplierActivity,
  useSelectedOrder,
  useFinalizeAdjudication,
} from "../../hooks/useOrders";
import type { FinalizeAdjudicationResponse } from "../../hooks/useOrders";
import {
  useCreateChat,
  useCreateMessage,
  useOrderChat,
} from "../../hooks/useChat";
import { useAcceptOffer, useOffersOnOrders } from "../../hooks/useOffers";
import { useInvitedOrderSuppliers } from "../../hooks/useSuppliers";
import { useAwardableNegotiations } from "../../hooks/useAwardableNegotiations";
import CreateOcModal from "../PurchaseOrders/CreateOc/CreateOcModal";
import { queryKeys } from "../../hooks/queryKeys";
import { useNotification, useUser } from "../../context";
import { ABILITIES, formatBiddingDateTime } from "../../utils";
import "./bid.scss";
import ServicesToQuoteCard from "../../components/ServicesToQuoteCard/ServicesToQuoteCard";
import { servicesToQuoteOf } from "../Order/serviceDetail";

const BuyerBid = () => {
  const { can } = useUser();
  const canManageOrder = can(ABILITIES.CREATE_ORDERS);
  const { openNotification } = useNotification();
  const { t } = useTranslation();
  const id = useID();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [openSlide, setOpenSlide] = useState(false);
  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");
  const [itemToSlide, setItemToSlide] = useState<OfferResponse | null>(null);
  const [isAward, setIsAward] = useState(false);
  const [isViewingAward, setIsViewingAward] = useState(false);
  const [createOcOpen, setCreateOcOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data: order, isLoading: isLoadingOrders } = useOrder(id);
  const { data: activityLogs = [] } = useOrderActivityLog(id);
  const { data: offersOnOrders, isLoading: isLoadingOffers } =
    useOffersOnOrders(id);
  const isTendersSucceded = offersOnOrders?.content?.find(
    (el: OfferResponse) => el.selected
  );
  const { data: orderSelected } = useSelectedOrder(id);
  const { data: chat, isLoading: isLoadingChat } = useOrderChat(id);
  const { data: invitedOrderSuppliers } = useInvitedOrderSuppliers(id);
  const { data: supplierActivity } = useOrderSupplierActivity(id);

  // An awarded order offers "Crear OC" only while it still has no OC — i.e. while it appears in the
  // awardable-negotiations list (a generated OC drops it). The query is only hit for awarded orders
  // the user may turn into an OC, and already respects the company's OC-module activation cutoff.
  const canCreatePO = can(ABILITIES.CREATE_PURCHASE_ORDERS);
  const isAwardedOrder = order?.state === OrderState.SUCCEED;
  const { data: awardable = [] } = useAwardableNegotiations(
    Boolean(isAwardedOrder && canCreatePO)
  );
  const hasOcCandidate = awardable.some(
    (c) => c.multipleOrderId == null && c.orderId === order?.id
  );

  const { mutate: mutateChat, isPending: isCreatingChat } = useCreateChat([
    queryKeys.CHAT,
  ]);

  const { mutate: mutateMessage, isPending: isSendingMessage } =
    useCreateMessage([queryKeys.CHAT]);

  const { mutate: acceptOffer, isPending: isAcceptingOffer } = useAcceptOffer(
    () => {
      // Close the award panel and refresh every view the award changes, so the page lands on the
      // freshly-awarded state (order state, selected offer, received offers, activity log) instead
      // of leaving the buyer on a stale screen.
      setIsAward(false);
      queryClient.invalidateQueries({
        queryKey: [queryKeys.OFFERS_ON_ORDERS, id],
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.TENDER, id] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.ORDER, id] });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ORDER_ACTIVITY_LOG, id],
      });
      return openNotification(t("tenders.data.acceptOffer"));
    },
    () => openNotification(t("newMaterialForm.error"))
  );

  const { mutate: deleteOrder, isPending: isDeleting } = useDeleteOrder(
    () => {
      openNotification(t("tenders.data.cancelMessage"));
      return navigate("/orders");
    },
    () => openNotification(t("newMaterialForm.error"))
  );

  const { mutate: downloadBidReport, isPending: isDownloadingBidReport } =
    useDownloadBidReport();
  const { mutate: downloadComparison, isPending: isDownloadingComparison } =
    useDownloadOrderComparison();

  // Post-quotation flow: finalize adjudication / launch 2nd round.
  const [secondRoundOpen, setSecondRoundOpen] = useState(false);

  const [finalizeRecap, setFinalizeRecap] =
    useState<FinalizeAdjudicationResponse | null>(null);

  const { mutate: finalizeAdjudication, isPending: isFinalizing } =
    useFinalizeAdjudication(
      (data) => {
        openNotification(t("tenders.data.adjudicationFinalized"));
        setFinalizeRecap(data);
      },
      () => openNotification(t("newMaterialForm.error"))
    );

  const closeFinalizeRecap = () => {
    setFinalizeRecap(null);
    navigate("/orders");
  };

  const handleCancel = () => {
    if (!cancelMessage) return;
    if (!order) return;
    deleteOrder({
      orderId: order.id,
      order: {
        ...order,
        comment: cancelMessage,
        state: OrderState.CANCELLED,
      },
    });
    setOpen(false);
  };

  const handleItemClick = (item: OfferResponse) => {
    if (order?.state === OrderState.OPEN) return;
    setItemToSlide(item);
    setOpenSlide(true);
  };

  // Not `isAcceptingOffer`: the award mutation shows its loading state on the confirm dialog's
  // button, so the panel stays visible instead of the whole page flashing to a full-screen loader.
  if (isLoadingOrders || isLoadingOffers || isDeleting)
    return <Loader isFullScreen />;

  if (!order) {
    return <NetworkError title={t("offer.orderNotFound")} />;
  }

  const canAddSupplier =
    canManageOrder &&
    (order.state === OrderState.OPEN ||
      order.state === OrderState.CONSULTATION);

  const isAwarded = order.state === OrderState.SUCCEED;

  const summaryTile = (
    <OfferSummaryTile
      totalOffers={offersOnOrders?.content?.length || 0}
      materialsCount={1}
      suppliersCount={
        (order?.suppliers?.length || 0) + (invitedOrderSuppliers?.length || 0)
      }
    />
  );

  const receivedOffersTile = (
    <Tile className="bid-tile">
      <h3 className="bid-tile__title">{t("tenders.data.receivedOffers")}</h3>
      <OffersOnOrders
        offOnOrd={offersOnOrders?.content}
        handleItemClick={handleItemClick}
        state={order.state as OrderState}
        activity={supplierActivity}
      />
    </Tile>
  );

  const activityTile = (
    <Tile className="bid-tile">
      <h3 className="bid-tile__title">
        {t("activityLog.title", { defaultValue: "Activity Log" })}
      </h3>
      <ActivityTimeline logs={activityLogs} purchaseRequestId={id || ""} />
    </Tile>
  );

  return (
    <Fragment>
      <FinalizeRecapModal recap={finalizeRecap} onClose={closeFinalizeRecap} />

      <CancelOrderDialog
        open={open}
        cancelMessage={cancelMessage}
        onChangeCancelMessage={setCancelMessage}
        onConfirm={handleCancel}
        onClose={() => setOpen(false)}
        isPending={isDeleting}
        inputId="bid-cancel-comment"
      />

      <SecondRoundModal
        open={secondRoundOpen}
        onClose={() => setSecondRoundOpen(false)}
        orderId={order.id}
        onLaunched={() => {
          setSecondRoundOpen(false);
          queryClient.invalidateQueries({ queryKey: [queryKeys.ORDER, id] });
          queryClient.invalidateQueries({ queryKey: [queryKeys.OFFERS_ON_ORDERS, id] });
          queryClient.invalidateQueries({ queryKey: [queryKeys.ORDER_ACTIVITY_LOG, id] });
        }}
      />

      <div className="bid-page">
        <div className="bid-page__section">
          <GoBackArrow />
        </div>

        <Tile className="bid-tile bid-page__header">
          <div className="bid-page__header-main">
            <BidHeader orderName={order.name} />
            <p className="bid-tile__info-value">
              {t("tenders.tenderSummary.start")} {formatBiddingDateTime(order.opensAt, order.startDate)}
              {" - "}
              {t("tenders.tenderSummary.end")} {formatBiddingDateTime(order.closesAt, order.finishDate)}
            </p>
            <Pill order={order} />
            <SecondRoundTag round={order.round} />
          </div>

          <BidToolbar
            canManageOrder={canManageOrder}
            isAward={isAward}
            onCancelAward={() => setIsAward(false)}
          showCancelOrder={
            order.state !== OrderState.CANCELLED &&
            order.state !== OrderState.SUCCEED
          }
          onCancelOrder={() => setOpen(true)}
          chatCount={chat?.messages?.length || 0}
          onOpenChat={() => setOpenChat(true)}
          showInvite={
            order.state !== OrderState.CANCELLED &&
            order.state !== OrderState.SUCCEED &&
            order.state !== OrderState.CLOSED
          }
          onInvite={() => setOpenInviteModal(true)}
          showDownloadComparison={
            order.state === OrderState.SUCCEED ||
            order.state === OrderState.CLOSED
          }
          onDownloadComparison={() => downloadComparison(id)}
          isDownloadingComparison={isDownloadingComparison}
          showDownloadBidReport={order.state === OrderState.SUCCEED}
          onDownloadBidReport={() => downloadBidReport(id)}
          isDownloadingBidReport={isDownloadingBidReport}
          showDeliveries={order.state === OrderState.SUCCEED}
          deliveriesLink="/deliveries/new"
          showCreateOc={canCreatePO && hasOcCandidate}
          onCreateOc={() => setCreateOcOpen(true)}
          showAwardTrigger={
            !isTendersSucceded &&
            order.state === OrderState.CLOSED &&
            Boolean(
              offersOnOrders?.content && offersOnOrders.content.length > 0
            )
          }
          onAwardTrigger={() => setIsAward(true)}
          showSecondRound={
            order.state === OrderState.CLOSED && !isTendersSucceded
          }
          onSecondRound={() => setSecondRoundOpen(true)}
          showFinalize={order.state === OrderState.CLOSED}
          onFinalize={() => finalizeAdjudication(order.id)}
            isFinalizing={isFinalizing}
          />
        </Tile>

        {isAward || isViewingAward ? (
          offersOnOrders?.content && (
            <Award
              order={order}
              offers={offersOnOrders?.content}
              onAcceptOffer={acceptOffer}
              onCancel={() => {
                setIsAward(false);
                setIsViewingAward(false);
              }}
              isAwarding={isAcceptingOffer}
              readOnly={isViewingAward}
            />
          )
        ) : (
          <BidAwardedTabs
            firstTabLabel={
              isAwarded
                ? t("tenders.data.tabAdjudication")
                : t("tenders.data.tabSummary")
            }
            adjudication={
              <>
                {isAwarded && Boolean(offersOnOrders?.content?.length) && (
                  <div className="bid-page__view-award">
                    <Button
                      kind="tertiary"
                      size="sm"
                      onClick={() => setIsViewingAward(true)}
                    >
                      {t("award.viewComparison")}
                    </Button>
                  </div>
                )}
                {isAwarded && orderSelected && isTendersSucceded && (
                  <BidResultTable
                    title={t("tenders.seller.acceptedJoint")}
                    selectedOffers={orderSelected}
                    motive={order?.offerSelectionComment}
                    fileUrl={order?.offerSelectionFileUrl}
                  />
                )}
                {summaryTile}
                <OrderConditionsTile order={order} />
                <ServicesToQuoteCard services={servicesToQuoteOf([order])} />
                <MaterialsOverviewTile order={order} />
                {order.state === OrderState.CLOSED &&
                  offersOnOrders?.content?.length === 0 && (
                    <AlertBanner title={"tenders.data.noOffersMessage"} />
                  )}
                {order.state === OrderState.CANCELLED && order.comment && (
                  <AlertBanner
                    title={"tenders.data.cancelledMessage"}
                    comments={order.comment}
                  />
                )}
              </>
            }
            suppliers={
              <>
                <SuppliersSelected
                  suppliers={order.suppliers}
                  invitedSuppliers={invitedOrderSuppliers}
                  activity={supplierActivity}
                  // C5: the buyer can add a supplier while the quotation is running — during
                  // bidding (OPEN) and the Q&A window (CONSULTATION) — but not once it has
                  // closed/adjudicated.
                  onAddSupplier={
                    canAddSupplier ? () => setOpenInviteModal(true) : undefined
                  }
                />
                {receivedOffersTile}
              </>
            }
            audit={activityTile}
          />
        )}
      </div>

      <ComposedModal
        open={openSlide}
        onClose={() => setOpenSlide(false)}
        size="md"
      >
        <ModalHeader title={t("offer.detailTitle")} />
        <ModalBody>{itemToSlide && <Offer offer={itemToSlide} />}</ModalBody>
      </ComposedModal>

      <ComposedModal
        open={openChat}
        onClose={() => setOpenChat(false)}
        size="md"
      >
        <ModalHeader title={t("chat.title")} />
        <ModalBody hasForm>
          <Chat
            isOrder
            chat={chat}
            order={order}
            onCreateChat={mutateChat}
            isLoadingChat={isLoadingChat || isSendingMessage || isCreatingChat}
            onCreateMessage={mutateMessage}
          />
        </ModalBody>
      </ComposedModal>

      <ComposedModal
        open={openInviteModal}
        onClose={() => setOpenInviteModal(false)}
        size="sm"
      >
        <ModalHeader title={t("orderByRequest.inviteSupplier")} />
        <ModalBody hasForm>
          <InviteSupplier
            existingOrderIds={[order.id]}
            materialId={order.material?.id}
            materialName={order.material?.globalMaterialName}
            onCloseInvitation={() => setOpenInviteModal(false)}
          />
        </ModalBody>
      </ComposedModal>

      <CreateOcModal
        open={createOcOpen}
        onClose={() => setCreateOcOpen(false)}
        filter={{ orderId: order.id }}
      />
    </Fragment>
  );
};

export default BuyerBid;
